import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLocationUrlSync } from '@/contexts/LocationContext';
import { Clock4, LogIn, LogOut, ChevronLeft, Wifi, WifiOff, X, Search, AlertTriangle, ArrowLeft, QrCode, List, ScanLine } from 'lucide-react';
import { readQueue, enqueue, clearQueue, queueSize } from '@/lib/clockQueue';

function useOnlineStatus() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    function on() { setOnline(true); }
    function off() { setOnline(false); }
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export default function Clock() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const online = useOnlineStatus();
  const { slug: selectedSlug, setSlug: setSelectedSlug } = useLocationUrlSync();

  // Force dark theme on html/body for clock page
  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#020617'; // slate-950
    document.documentElement.style.backgroundColor = '#020617';
    return () => {
      document.body.style.backgroundColor = prev;
      document.documentElement.style.backgroundColor = '';
    };
  }, []);
  const [locations, setLocations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState([]); // currently clocked-in
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState({ type: '', msg: '', name: '' });
  const [queuedCount, setQueuedCount] = useState(queueSize());
  const [syncMsg, setSyncMsg] = useState('');
  const [qrMode, setQrMode] = useState(false);
  const [qrOnly, setQrOnly] = useState(false);
  const [qrInput, setQrInput] = useState('');
  const [qrBusy, setQrBusy] = useState(false);
  const qrInputRef = React.useRef(null);

  useEffect(() => {
    if (qrMode && qrInputRef.current) qrInputRef.current.focus();
  }, [qrMode, feedback]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get('/locations');
      const elig = data.filter((l) => l.has_clock !== false);
      setLocations(elig);
    })();
  }, []);

  // QR-only inklokken (fraude-preventie). When enabled, list/tap self-clock is
  // fully disabled in the UI and force QR-scan mode (the API blocks it too).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/system-config/qr_only_clock');
        const enabled = data?.value?.enabled !== false;
        setQrOnly(enabled);
        if (enabled) setQrMode(true);
      } catch (e) {
        console.warn('[clock] qr_only config load failed, defaulting to QR-only', e);
        setQrOnly(true);
        setQrMode(true);
      }
    })();
  }, []);

  // Fallback selection (URL-aware via context).
  useEffect(() => {
    if (locations.length > 0 && !selectedSlug) {
      setSelectedSlug(locations[0].slug);
    }
  }, [locations, selectedSlug, setSelectedSlug]);

  const load = useCallback(async () => {
    if (!selectedSlug) return;
    try {
      const [empResp, statusResp] = await Promise.all([
        api.get(`/employees?location_slug=${selectedSlug}&status=actief`),
        api.get(`/clock/${selectedSlug}/status`),
      ]);
      setEmployees(empResp.data);
      setOpen(statusResp.data.currently_clocked_in || []);
    } catch (e) { console.error('[clock] load failed', e); }
  }, [selectedSlug]);

  useEffect(() => { load(); }, [load]);

  // Sync offline queue when online + heartbeat every 60s
  const flushQueue = useCallback(async () => {
    const q = readQueue();
    if (q.length === 0 || !online || !selectedSlug) return;
    try {
      const { data } = await api.post('/clock/sync', { location_id: selectedSlug, events: q });
      clearQueue();
      setQueuedCount(0);
      setSyncMsg(`${data.accepted} events gesynchroniseerd${data.duplicates ? ` (${data.duplicates} duplicate)` : ''}`);
      setTimeout(() => setSyncMsg(''), 4000);
      await load();
    } catch (e) {
      console.warn('[clock] sync failed, keeping queue', e);
    }
  }, [online, selectedSlug, load]);

  useEffect(() => {
    flushQueue();
    const timer = setInterval(flushQueue, 60000);
    return () => clearInterval(timer);
  }, [flushQueue]);

  const openByEmpId = useMemo(() => Object.fromEntries(open.map((r) => [r.employee_id, r])), [open]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter(
      (e) => e.name.toLowerCase().includes(term) || e.personeelsnummer.includes(term),
    );
  }, [employees, search]);

  async function handleClockToggle(emp) {
    const isIn = !!openByEmpId[emp.id];
    const action = isIn ? 'clock_out' : 'clock_in';
    const endpoint = isIn ? 'clock-out' : 'clock-in';

    if (!online) {
      enqueue({ employee_id: emp.id, action, timestamp_local: new Date().toISOString() });
      setQueuedCount(queueSize());
      setFeedback({
        type: 'info', name: emp.name,
        msg: isIn ? 'Uitgeklokt (offline — wordt gesynchroniseerd)' : 'Ingeklokt (offline — wordt gesynchroniseerd)',
      });
      setTimeout(() => setFeedback({ type: '', msg: '', name: '' }), 2500);
      return;
    }

    try {
      const { data } = await api.post(`/clock/${selectedSlug}/${endpoint}`, { employee_id: emp.id });
      setFeedback({
        type: 'success', name: data.employee_name,
        msg: isIn ? 'Uitgeklokt' : 'Ingeklokt',
      });
      setTimeout(() => setFeedback({ type: '', msg: '', name: '' }), 2500);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Mislukt';
      setFeedback({ type: 'error', name: emp.name, msg: typeof detail === 'string' ? detail : detail.message || 'Mislukt' });
      setTimeout(() => setFeedback({ type: '', msg: '', name: '' }), 3500);
    }
  }

  async function handleQrSubmit(e) {
    e.preventDefault();
    const token = qrInput.trim();
    if (!token || qrBusy) return;
    setQrInput('');
    if (!online) {
      setFeedback({ type: 'error', name: 'QR', msg: 'QR scannen vereist internet — gebruik de lijst' });
      setTimeout(() => setFeedback({ type: '', msg: '', name: '' }), 3500);
      return;
    }
    setQrBusy(true);
    try {
      const clientEventId = (window.crypto?.randomUUID?.() || `qr-${Date.now()}-${Math.random()}`);
      const { data } = await api.post(`/clock/${selectedSlug}/qr-clock`, { qr_token: token, client_event_id: clientEventId });
      setFeedback({
        type: 'success', name: data.employee_name,
        msg: data.action === 'clock_out' ? 'Uitgeklokt' : 'Ingeklokt',
      });
      setTimeout(() => setFeedback({ type: '', msg: '', name: '' }), 2500);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = err.response?.status === 401 ? 'Ongeldige of verlopen QR-code'
        : (typeof detail === 'string' ? detail : detail?.message || 'Mislukt');
      setFeedback({ type: 'error', name: 'QR', msg });
      setTimeout(() => setFeedback({ type: '', msg: '', name: '' }), 3500);
    } finally {
      setQrBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans" data-testid="page-clock">
      <header className="flex items-center justify-between border-b border-slate-800 px-6 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1 rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800">
            <ArrowLeft size={12} /> Terug
          </button>
          <div className="flex items-center gap-2">
            <Clock4 className="text-brand-amber" size={22} />
            <h1 className="text-base font-semibold">Inklokken</h1>
          </div>
          <select
            value={selectedSlug}
            onChange={(e) => setSelectedSlug(e.target.value)}
            data-testid="clock-select-location"
            className="rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-100"
          >
            {locations.map((l) => (
              <option key={l.slug} value={l.slug}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-4 text-sm">
          {!qrOnly && (
          <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid="clock-mode-toggle">
            <button
              onClick={() => setQrMode(false)}
              data-testid="clock-mode-list"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs ${!qrMode ? 'bg-brand-amber text-slate-900 font-semibold' : 'text-slate-300 hover:bg-slate-800'}`}
            >
              <List size={13} /> Lijst
            </button>
            <button
              onClick={() => setQrMode(true)}
              data-testid="clock-mode-qr"
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs ${qrMode ? 'bg-brand-amber text-slate-900 font-semibold' : 'text-slate-300 hover:bg-slate-800'}`}
            >
              <QrCode size={13} /> QR scannen
            </button>
          </div>
          )}
          {qrOnly && (
            <span className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 px-3 py-1.5 text-xs font-medium text-brand-amber" data-testid="clock-qr-only-badge">
              <QrCode size={13} /> Alleen QR-inklokken
            </span>
          )}
          <div className="flex items-center gap-1" data-testid="clock-online-status">
            {online ? <><Wifi size={14} className="text-green-400" /> Online</> : <><WifiOff size={14} className="text-red-400" /> Offline</>}
          </div>
          {queuedCount > 0 && (
            <span className="rounded-full bg-amber-500/20 px-3 py-1 text-xs font-medium text-amber-300" data-testid="clock-queue-count">
              {queuedCount} in wachtrij
            </span>
          )}
        </div>
      </header>

      {syncMsg && (
        <div className="bg-green-900/40 border-b border-green-700 px-6 py-2 text-sm text-green-200" data-testid="sync-msg">{syncMsg}</div>
      )}

      <div className="px-6 py-4">
        {!qrMode && (
        <div className="flex items-center gap-2 mb-4 rounded-lg bg-slate-900 border border-slate-700 px-3">
          <Search size={16} className="text-slate-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek naam of personeelsnummer…"
            data-testid="clock-search"
            className="flex-1 bg-transparent border-0 py-3 text-base focus:outline-none placeholder-slate-500"
          />
          {search && <button onClick={() => setSearch('')}><X size={14} /></button>}
        </div>
        )}

        {feedback.msg && (
          <div
            className={`mb-4 flex items-center gap-3 rounded-lg px-4 py-3 text-sm ${
              feedback.type === 'success' ? 'bg-green-900/40 text-green-200 border border-green-700' :
              feedback.type === 'error' ? 'bg-red-900/40 text-red-200 border border-red-700' :
              'bg-amber-900/40 text-amber-200 border border-amber-700'
            }`}
            data-testid="clock-feedback"
          >
            {feedback.type === 'error' && <AlertTriangle size={14} />}
            <strong>{feedback.name}:</strong> {feedback.msg}
          </div>
        )}

        {qrMode && (
          <div className="mx-auto max-w-md py-6" data-testid="clock-qr-panel">
            <form onSubmit={handleQrSubmit} className="rounded-2xl border border-slate-700 bg-slate-900 p-8 text-center">
              <ScanLine size={48} className="mx-auto mb-4 text-brand-amber" />
              <h2 className="text-lg font-semibold mb-1">Scan je QR-code</h2>
              <p className="text-sm text-slate-400 mb-5">Houd je persoonlijke QR-code voor de scanner — of voer de code handmatig in en druk op Enter.</p>
              <input
                ref={qrInputRef}
                type="text"
                value={qrInput}
                onChange={(e) => setQrInput(e.target.value)}
                placeholder="QR-code / token…"
                data-testid="clock-qr-input"
                autoComplete="off"
                className="w-full rounded-lg border border-slate-600 bg-slate-950 px-4 py-3 text-center text-base tracking-wider focus:border-brand-amber focus:outline-none placeholder-slate-600"
              />
              <button
                type="submit"
                disabled={qrBusy || !qrInput.trim()}
                data-testid="clock-qr-submit"
                className="mt-4 w-full rounded-lg bg-brand-amber px-4 py-3 text-base font-semibold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
              >
                {qrBusy ? 'Bezig…' : 'Inklokken / Uitklokken'}
              </button>
            </form>
          </div>
        )}

        {!qrMode && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filtered.map((emp) => {
            const isIn = !!openByEmpId[emp.id];
            return (
              <button
                key={emp.id}
                onClick={() => handleClockToggle(emp)}
                data-testid={`clock-emp-${emp.personeelsnummer}`}
                className={`flex flex-col items-start gap-1 rounded-xl border p-4 text-left transition-colors ${
                  isIn
                    ? 'border-green-600 bg-green-900/30 hover:bg-green-900/40'
                    : 'border-slate-700 bg-slate-900 hover:border-brand-amber hover:bg-slate-800'
                }`}
              >
                <div className="flex w-full items-center justify-between">
                  <p className="text-base font-semibold">{emp.name}</p>
                  {isIn ? (
                    <span className="rounded bg-green-700 px-2 py-0.5 text-[10px] uppercase tracking-wide">In</span>
                  ) : (
                    <span className="rounded border border-slate-600 px-2 py-0.5 text-[10px] uppercase tracking-wide">Uit</span>
                  )}
                </div>
                <p className="text-xs text-slate-400">Pnr {emp.personeelsnummer}</p>
                <div className="mt-2 flex items-center gap-1 text-xs text-slate-300">
                  {isIn ? <><LogOut size={12} /> Klok uit</> : <><LogIn size={12} /> Klok in</>}
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <p className="col-span-full py-10 text-center text-sm text-slate-500">Geen medewerkers gevonden voor &ldquo;{search}&rdquo;</p>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
